<script setup lang="ts">
import type { LocalizedLabel, PublicMenuItem, TenantContext } from '~/types/types'

/**
 * Tenant public-site header — themed with --site-* tokens. Shows the
 * tenant identity, the site menu (dynamic pages arrive in a later phase),
 * locale + light/dark toggles, and auth actions.
 */
const props = defineProps<{ tenant: TenantContext }>()

const { t, locale } = useI18n()
const localePath = useLocalePath()
const switchLocalePath = useSwitchLocalePath()
const auth = useAuth()
const route = useRoute()

const { mode, toggle } = useSiteMode(() => props.tenant.site)

const mobileOpen = ref(false)
watch(() => route.fullPath, () => {
  mobileOpen.value = false
})

const isAuthenticated = computed(() => Boolean(auth.user.value))
const dashboardPath = computed(() => auth.getPostLoginRedirect())

// This header renders on a tenant's own public site — visitors here are the
// tenant's clients, not the agency's staff. Login/Register previously
// pointed at /login and /register (staff auth), so a prospect clicking
// "Login" landed in the wrong app entirely. Staff still reach their own
// login by URL/bookmark; a logged-in staff member still sees the Dashboard
// button below via `isAuthenticated`, which tracks the staff session only.
const clientLoginPath = (path: '/login' | '/register') =>
  localePath(`/client/${props.tenant.slug}${path}`)

// Menu built in /agent/site/menu; falls back to Home when empty. A "label"
// item is a plain dropdown title with no page of its own — it only opens
// its children.
//
// Labels are stored bilingually ({ mn, en }); resolving here (rather than
// server-side) means the nav flips instantly on a language switch — the
// tenant-context fetch is cached independent of locale, so a server-resolved
// string would go stale until a hard reload.
const localizedLabel = (label: LocalizedLabel) =>
  label[locale.value as 'mn' | 'en'] || label.mn || label.en || ''

const mapMenuLink = (item: PublicMenuItem) => ({
  label: localizedLabel(item.label),
  to: item.kind === 'external'
    ? (item.url || '#')
    : item.kind === 'page'
      ? localePath(`/${item.page_slug}`)
      : item.kind === 'landing'
        ? localePath('/')
        : null,
  external: item.kind === 'external',
  newTab: item.kind === 'external' && item.open_in_new
})

const menuItems = computed(() => {
  const items = props.tenant.menu ?? []
  if (!items.length) {
    return [{ label: t('site.nav_home'), to: localePath('/'), external: false, newTab: false, children: [] }]
  }
  return items.map(item => ({
    ...mapMenuLink(item),
    children: (item.children ?? []).map(mapMenuLink)
  }))
})

const toDropdownItems = (children: ReturnType<typeof mapMenuLink>[]) =>
  children.map(c => ({ label: c.label, to: c.to ?? undefined, external: c.external, target: c.newTab ? '_blank' : undefined }))

// Highlights the current page in the nav. Home ("/") only counts as active
// on an exact match so it doesn't stay lit up on every other page.
const isPathActive = (to?: string | null) => {
  if (!to) return false
  const path = route.path.replace(/\/$/, '') || '/'
  const target = to.replace(/\/$/, '') || '/'
  return path === target
}
const isItemActive = (item: { to?: string | null, children?: { to?: string | null }[] }) =>
  isPathActive(item.to) || (item.children?.some(c => isPathActive(c.to)) ?? false)
</script>

<template>
  <header class="sticky top-0 z-50 border-b border-[var(--site-border)] bg-[var(--site-bg)]/90 backdrop-blur-md">
    <nav
      aria-label="Site navigation"
      class="mx-auto flex max-w-6xl items-center justify-between gap-4 px-6 py-3"
    >
      <div class="flex items-center gap-6">
        <NuxtLink
          :to="localePath('/')"
          class="flex shrink-0 items-center gap-2.5"
        >
          <img
            v-if="tenant.branding.logo_url"
            :src="tenant.branding.logo_url"
            :alt="`${tenant.name} logo`"
            class="size-8 rounded-[calc(var(--site-radius)*0.5)] object-contain"
          >
          <span
            v-else
            class="site-btn-accent flex size-8 items-center justify-center text-sm font-bold"
          >{{ (tenant.name || '?').charAt(0).toUpperCase() }}</span>
          <span class="site-display text-base tracking-tight">{{ tenant.name }}</span>
        </NuxtLink>

        <div class="hidden items-center gap-1 lg:flex">
          <template
            v-for="item in menuItems"
            :key="item.label + (item.to ?? '')"
          >
            <UDropdownMenu
              v-if="item.children?.length"
              :items="toDropdownItems(item.children)"
              :content="{ align: 'start' }"
            >
              <button
                type="button"
                class="flex items-center gap-1 rounded-[var(--site-radius)] px-3 py-1.5 text-sm font-medium transition hover:bg-[var(--site-accent-soft)] hover:text-[var(--site-fg)]"
                :class="isItemActive(item) ? 'bg-[var(--site-accent-soft)] font-semibold text-[var(--site-accent)]' : 'text-[var(--site-muted)]'"
              >
                {{ item.label }}
                <UIcon
                  name="i-lucide-chevron-down"
                  class="size-3.5"
                />
              </button>
            </UDropdownMenu>
            <NuxtLink
              v-else-if="item.to"
              :to="item.to"
              :external="item.external"
              :target="item.newTab ? '_blank' : undefined"
              class="rounded-[var(--site-radius)] px-3 py-1.5 text-sm font-medium transition hover:bg-[var(--site-accent-soft)] hover:text-[var(--site-fg)]"
              :class="isPathActive(item.to) ? 'bg-[var(--site-accent-soft)] font-semibold text-[var(--site-accent)]' : 'text-[var(--site-muted)]'"
            >
              {{ item.label }}<UIcon
                v-if="item.newTab"
                name="i-lucide-arrow-up-right"
                class="ml-0.5 inline size-3.5 align-text-top"
              />
            </NuxtLink>
            <span
              v-else
              class="rounded-[var(--site-radius)] px-3 py-1.5 text-sm font-medium text-[var(--site-muted)]/60"
            >
              {{ item.label }}
            </span>
          </template>
        </div>
      </div>

      <div class="hidden items-center gap-1.5 lg:flex">
        <NuxtLink
          :to="switchLocalePath(locale === 'mn' ? 'en' : 'mn')"
          class="flex items-center gap-1 rounded-[var(--site-radius)] px-2.5 py-1.5 text-sm font-medium uppercase text-[var(--site-muted)] transition hover:bg-[var(--site-accent-soft)] hover:text-[var(--site-fg)]"
        >
          <UIcon
            name="i-lucide-globe"
            class="size-4"
          />
          {{ locale }}
        </NuxtLink>

        <ClientOnly>
          <button
            type="button"
            class="flex size-9 items-center justify-center rounded-[var(--site-radius)] text-[var(--site-muted)] transition hover:bg-[var(--site-accent-soft)] hover:text-[var(--site-fg)]"
            :aria-label="t('site.toggle_mode')"
            @click="toggle"
          >
            <UIcon
              :name="mode === 'dark' ? 'i-lucide-sun' : 'i-lucide-moon'"
              class="size-4.5"
            />
          </button>
        </ClientOnly>

        <span class="mx-1 h-5 w-px bg-[var(--site-border)]" />

        <template v-if="!isAuthenticated">
          <NuxtLink
            :to="clientLoginPath('/login')"
            class="rounded-[var(--site-radius)] px-3 py-1.5 text-sm font-medium text-[var(--site-muted)] transition hover:bg-[var(--site-accent-soft)] hover:text-[var(--site-fg)]"
          >
            {{ t('auth.login') }}
          </NuxtLink>
          <NuxtLink
            :to="clientLoginPath('/register')"
            class="site-btn-accent px-4 py-1.5 text-sm font-semibold transition"
          >
            {{ t('auth.signup') }}
          </NuxtLink>
        </template>
        <NuxtLink
          v-else
          :to="dashboardPath"
          class="site-btn-outline px-4 py-1.5 text-sm font-semibold transition"
        >
          {{ t('app_shell.dashboard') }}
        </NuxtLink>
      </div>

      <button
        type="button"
        class="flex size-9 items-center justify-center rounded-[var(--site-radius)] text-[var(--site-fg)] lg:hidden"
        :aria-expanded="mobileOpen"
        aria-label="Toggle navigation menu"
        @click="mobileOpen = !mobileOpen"
      >
        <UIcon
          :name="mobileOpen ? 'i-lucide-x' : 'i-lucide-menu'"
          class="size-5"
        />
      </button>
    </nav>

    <div
      v-if="mobileOpen"
      class="border-t border-[var(--site-border)] bg-[var(--site-bg)] px-6 py-4 lg:hidden"
    >
      <div class="space-y-1">
        <template
          v-for="item in menuItems"
          :key="item.label + (item.to ?? '')"
        >
          <NuxtLink
            v-if="item.to"
            :to="item.to"
            :external="item.external"
            :target="item.newTab ? '_blank' : undefined"
            class="block rounded-[var(--site-radius)] px-3 py-2 text-sm font-medium hover:bg-[var(--site-accent-soft)]"
            :class="isItemActive(item) ? 'bg-[var(--site-accent-soft)] text-[var(--site-accent)]' : 'text-[var(--site-fg)]'"
          >
            {{ item.label }}
          </NuxtLink>
          <span
            v-else
            class="block px-3 py-2 text-sm font-medium text-[var(--site-fg)]/60"
          >
            {{ item.label }}
          </span>
          <NuxtLink
            v-for="child in item.children"
            :key="child.label + (child.to ?? '')"
            :to="child.to ?? undefined"
            :external="child.external"
            :target="child.newTab ? '_blank' : undefined"
            class="block rounded-[var(--site-radius)] px-3 py-2 pl-6 text-sm hover:bg-[var(--site-accent-soft)] hover:text-[var(--site-fg)]"
            :class="isPathActive(child.to) ? 'bg-[var(--site-accent-soft)] font-semibold text-[var(--site-accent)]' : 'text-[var(--site-muted)]'"
          >
            {{ child.label }}
          </NuxtLink>
        </template>
      </div>

      <div class="mt-3 flex items-center gap-2">
        <NuxtLink
          :to="switchLocalePath(locale === 'mn' ? 'en' : 'mn')"
          class="site-btn-outline flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium uppercase"
        >
          <UIcon
            name="i-lucide-globe"
            class="size-4"
          />
          {{ locale }}
        </NuxtLink>
        <ClientOnly>
          <button
            type="button"
            class="site-btn-outline flex size-9 items-center justify-center"
            :aria-label="t('site.toggle_mode')"
            @click="toggle"
          >
            <UIcon
              :name="mode === 'dark' ? 'i-lucide-sun' : 'i-lucide-moon'"
              class="size-4"
            />
          </button>
        </ClientOnly>
      </div>

      <div
        v-if="!isAuthenticated"
        class="mt-3 grid grid-cols-2 gap-2"
      >
        <NuxtLink
          :to="clientLoginPath('/login')"
          class="site-btn-outline px-3 py-2 text-center text-sm font-semibold"
        >
          {{ t('auth.login') }}
        </NuxtLink>
        <NuxtLink
          :to="clientLoginPath('/register')"
          class="site-btn-accent px-3 py-2 text-center text-sm font-semibold"
        >
          {{ t('auth.signup') }}
        </NuxtLink>
      </div>
      <NuxtLink
        v-else
        :to="dashboardPath"
        class="site-btn-outline mt-3 block px-3 py-2 text-center text-sm font-semibold"
      >
        {{ t('app_shell.dashboard') }}
      </NuxtLink>
    </div>
  </header>
</template>
